import type { KnownIntegrationKey } from "./agentTools/remote/integrations";
import type { SetupCheckName } from "./onboarding";
import type {
  WorkflowDefinition,
  workflowOptionIconSchema,
  workflowOptionSchema,
  workflowPageTypeSchema,
  workflowRequirementSchema,
  workflowStepBodySchema,
  workflowStepSchema,
} from "./workflowDefinition";

import mapValues from "lodash-es/mapValues";
import pickBy from "lodash-es/pickBy";
import * as z from "zod";

import { knownIntegrationKeys } from "./agentTools/remote/integrations";
import { WORKFLOW_FIXTURES } from "./generated/workflowFixtures";
import {
  byIntegrationWorkflow,
  INTEGRATION_WORKFLOW_NAMES,
} from "./integrationWorkflowKeys";
import { workflowVerticalSchema } from "./projectVertical";
import { ReploError } from "./reploError";
import { WORKFLOW_SLUGS } from "./workflowSlugs";

export const workflowSlugSchema = z
  .enum(WORKFLOW_SLUGS)
  .describe("WorkflowSlug");

export type WorkflowSlug = z.infer<typeof workflowSlugSchema>;

export function isWorkflowSlug(value: unknown): value is WorkflowSlug {
  return workflowSlugSchema.safeParse(value).success;
}

/**
 * Names workflows were stored under and are still read by: runs, onboarding picks, saved products and
 * templates, and links. Stored rows are never rewritten; every reader resolves through this map.
 * `copy-competitor-page` is not here although its runs were Shopify runs: the name now belongs to the base
 * workflow, and a run stored under a base is shown under whichever version the project is offered.
 */
export const LEGACY_WORKFLOW_SLUGS = {
  "build-shopify-landing-page": "shopify-build-landing-page",
  "build-shopify-product-page": "shopify-build-product-page",
  "build-shopify-listicle": "shopify-build-listicle",
  "build-shopify-advertorial": "shopify-build-advertorial",
  "build-shopify-bundle": "shopify-build-bundle",
  "copy-shopify-page": "shopify-copy-page",
  "import-shopify-figma": "shopify-import-figma",
  "build-shopify-ad-page": "shopify-build-ad-page",
  "build-shopify-persona-page": "shopify-build-persona-page",
  "build-shopify-ab-variant": "shopify-build-ab-variant",
  "suggest-shopify-pages": "shopify-suggest-pages",
} as const satisfies Record<string, WorkflowSlug>;

/** The workflow a stored or client-sent name means today, or null when it names none. */
export function resolveWorkflowSlug(value: unknown): WorkflowSlug | null {
  if (typeof value !== "string") {
    return null;
  }
  const renamed: Record<string, WorkflowSlug> = LEGACY_WORKFLOW_SLUGS;
  const current = Object.hasOwn(renamed, value) ? renamed[value] : value;
  return isWorkflowSlug(current) ? current : null;
}

/** A workflow slug from storage, a link or an older client: a legacy name is read as the workflow it became. */
export const storedWorkflowSlugSchema = z.preprocess((value) => {
  return resolveWorkflowSlug(value) ?? value;
}, workflowSlugSchema);

/** Any workflow a project can open, its own `custom-…` ones included; a legacy code name is read as the workflow it became. */
export const openableWorkflowSlugSchema = z.preprocess(
  (value) => resolveWorkflowSlug(value) ?? value,
  z
    .string()
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/)
    .max(64),
);

/**
 * A record keyed by workflow slug as it was stored: an entry under a legacy name is read under the current one,
 * and an entry already under the current name wins.
 */
export function storedWorkflowSlugRecord<Value extends z.ZodType>(
  value: Value,
) {
  return z.preprocess(
    (stored) => {
      if (typeof stored !== "object" || stored === null) {
        return stored;
      }
      const entries = Object.entries(stored);
      const current = new Set(entries.map(([key]) => key));
      return Object.fromEntries(
        entries.flatMap(([key, entry]) => {
          const slug = resolveWorkflowSlug(key) ?? key;
          return slug !== key && current.has(slug) ? [] : [[slug, entry]];
        }),
      );
    },
    z.partialRecord(workflowSlugSchema, value),
  );
}

/** My workflows holds the project's own workflows; Scale grows a store that already sells; Launch starts something new; Research studies other brands; Integrations brings Replo into other apps. */
export type HomeTab = Exclude<WorkflowTab, "build">;

/** A workflow's category; only the ones in HOME_TABS are drawn on Home. `build` holds the setup lists each app shows itself. */
export type WorkflowTab = WorkflowDefinition["tab"];

/**
 * The pills above the cards, in display order. Home shows a tab only when it has a card, so My workflows
 * appears with a project's first workflow of its own. Workflows on a tab missing here are not drawn on Home.
 */
export const HOME_TABS: { id: HomeTab; label: string }[] = [
  { id: "mine", label: "My workflows" },
  { id: "scale", label: "Scale" },
  { id: "launch", label: "Launch" },
  { id: "research", label: "Research" },
  { id: "integrations", label: "Integrations" },
];

/** The tabs a code workflow can be filed under: My workflows only ever holds a project's own. */
export const CODE_HOME_TABS = HOME_TABS.filter(
  (tab): tab is { id: Workflow["tab"] & HomeTab; label: string } => {
    return tab.id !== "mine";
  },
);

export function isHomeTab(value: unknown): value is HomeTab {
  return HOME_TABS.some((tab) => tab.id === value);
}

/** Plain names map to line icons, `app:` to Replo app icons, `integration:` to a tool logo. */
export type WorkflowOptionIcon = z.infer<typeof workflowOptionIconSchema>;

export type WorkflowOption = z.infer<typeof workflowOptionSchema>;

/** "options" is the default row of tiles; the rest are the custom bodies the landing-page and connect cards use. */
export type WorkflowStepBody = z.infer<typeof workflowStepBodySchema>;

export type WorkflowPageType = z.infer<typeof workflowPageTypeSchema>;

export type WorkflowStep = z.infer<typeof workflowStepSchema>;

/**
 * A prerequisite for being offered a workflow. `checkout/shopify` is a project that sells through Shopify,
 * `checkout/replo` one that sells through Replo; any other is an integration the project must have connected.
 */
export type WorkflowRequirement = z.infer<typeof workflowRequirementSchema>;

/**
 * A built-in workflow, from `workflow-fixtures`: what every global `workflow_definition` row holds. Its names are the closed
 * `WorkflowSlug` list, which code that draws or pays a specific workflow keys on. A workflow read from the database
 * is a `WorkflowDefinition`, whose slug can be any name, including a project's `custom-…` ones.
 */
export type Workflow = Omit<
  WorkflowDefinition,
  "slug" | "baseWorkflow" | "continues" | "tab" | "steps"
> & {
  slug: WorkflowSlug;
  /**
   * On a version that adds an integration: the base workflow it is built from. It is that workflow with the
   * integration's connect step first, and shares its templates, card art and reward. A project is offered
   * one version of a base, never two.
   */
  baseWorkflow?: WorkflowSlug;
  /**
   * On a workflow of one set: the workflow of another set it took the place of for its vertical. A run that
   * vertical's project started there is shown and continued as this workflow's, so it must keep every step slug and check.
   */
  continues?: WorkflowSlug;
  tab: Exclude<WorkflowTab, "mine">;
  steps: [WorkflowStep, ...WorkflowStep[]];
};

class WorkflowNotDefinedError extends ReploError {}

/** Every built-in workflow, from `workflow-fixtures/*.json`. */
export const WORKFLOWS: readonly Workflow[] = WORKFLOW_FIXTURES;

/** The versions built on a base workflow, each adding an integration; empty for a workflow that has none. */
export function getIntegrationVersions(slug: WorkflowSlug): Workflow[] {
  return WORKFLOWS.filter((workflow) => workflow.baseWorkflow === slug);
}

/** A workflow with its base and every version of that base: the workflows a project is offered exactly one of. */
export function getWorkflowFamily<
  Definition extends WorkflowDefinition = Workflow,
>(
  workflow: Pick<Definition, "slug" | "baseWorkflow">,
  workflows?: readonly Definition[],
): Definition[] {
  // Without a list, the family is looked up among the code workflows, which is what `Definition` defaults to.
  const list: readonly WorkflowDefinition[] = workflows ?? WORKFLOWS;
  const baseSlug = workflow.baseWorkflow ?? workflow.slug;
  return list.filter((candidate): candidate is Definition => {
    return candidate.slug === baseSlug || candidate.baseWorkflow === baseSlug;
  });
}

/** The integration a step connects, which is how a workflow's requirement is met; null for every other step. */
export function getStepIntegration(
  step: Pick<WorkflowStep, "body">,
): KnownIntegrationKey | null {
  return step.body === "shopify" ? "shopify" : null;
}

/** Every workflow still in the product: what onboarding and Home offer and draw. */
export const CURRENT_WORKFLOWS: readonly Workflow[] = WORKFLOWS.filter(
  (workflow) => !workflow.retired,
);

/** The line a merchant sees in the celebration toast when a workflow first pays out. */
export const WORKFLOW_COMPLETED_MESSAGES: Record<WorkflowSlug, string> = {
  "site-domain-setup": "You connected your site domain",
  "site-tracking-setup": "You set up site tracking",
  "site-seo-setup": "You set up site SEO",
  "product-setup": "You set up your product",
  "brand-setup": "You set up your brand",
  "asset-setup": "You set up your assets",
  "content-setup": "You set up your content",
  "canvas-setup": "You set up your canvas",
  "research-setup": "You completed your research setup",
  "file-setup": "You set up your files",
  "order-setup": "You set up your orders",
  "report-setup": "You set up your report",
  "plan-setup": "You set up your plan",
  "task-setup": "You set up your task",
  "skill-setup": "You set up your skill",
  "integration-setup": "You set up your integration",
  "shopify-build-landing-page": "You built your first Shopify landing page",
  "build-landing-page": "You built your first landing page",
  "build-product-page": "You built your first product page",
  "build-listicle": "You built your first listicle",
  "build-advertorial": "You built your first advertorial",
  "build-bundle": "You built your first bundle builder",
  "shopify-build-product-page": "You built your first Shopify product page",
  "shopify-build-listicle": "You built your first Shopify listicle",
  "shopify-build-advertorial": "You built your first Shopify advertorial",
  "shopify-build-bundle": "You built your first Shopify bundle builder",
  "copy-page": "You copied your first landing page",
  "copy-competitor-page": "You turned a competitor's page into your own",
  "import-figma": "You turned your first Figma design into a page",
  "shopify-copy-page": "You copied your first landing page",
  "shopify-copy-competitor-page":
    "You turned a competitor's page into your own",
  "start-shopify-template": "You built your first page from a template",
  "build-shopify-quiz": "You built your first product quiz",
  "audit-shopify-page": "You ran your first CRO page audit",
  "shopify-build-persona-page":
    "You built your first page for a customer persona",
  "shopify-build-ab-variant": "You built your first A/B test variant",
  "shopify-suggest-pages": "You planned your next pages",
  "shopify-import-figma": "You turned your first Figma design into a page",
  "shopify-build-ad-page": "You turned your first ad into a landing page",
  "saas-build-landing-page": "You built your first SaaS landing page",
  "saas-build-feature-page": "You built your first feature page",
  "saas-build-pricing-page": "You built your first pricing page",
  "saas-build-site": "You built your SaaS site from scratch",
  "saas-copy-page": "You copied your first SaaS landing page",
  "saas-copy-competitor-page": "You turned a competitor's page into your own",
  "saas-import-figma": "You turned your first Figma design into a page",
  "services-build-landing-page": "You built your first landing page",
  "services-build-booking-page": "You built your first booking page",
  "services-build-site": "You built a site for your business",
  "services-build-services-page": "You built your services and pricing page",
  "services-copy-page": "You copied your first page",
  "services-copy-competitor-page":
    "You turned a competitor's page into your own",
  "services-compare-competitor-offers": "You planned your next promotion",
  "services-find-creative-angles": "You wrote your first ad hooks",
  "research-competitor-ads": "You made your first ads from brands like yours",
  "research-competitor-pages": "You saved your first competitor pages",
  "compare-competitor-offers": "You planned your next promotion",
  "find-creative-angles": "You wrote your first ad hooks",
  "build-swipe-file": "You built your first campaign swipe file",
  "write-creative-brief": "You briefed your first video creator",
  "connect-store": "You connected your Shopify store",
  "connect-slack-or-assistant": "You connected Replo to your team's tools",
  "use-replo-from-slack": "You used Replo from Slack",
  "use-replo-from-claude": "You used Replo from Claude",
  "use-replo-from-chatgpt": "You used Replo from ChatGPT",
  ...byIntegrationWorkflow({
    prefix: "use-",
    value: (key) => `You used ${INTEGRATION_WORKFLOW_NAMES[key]} in Replo`,
  }),
  "build-sales-funnel": "You built your first sales funnel",
  "build-storefront": "You built your store from scratch",
  "find-product-to-sell": "You validated your first product idea",
  "import-shopify-products": "Your Shopify products are ready to use",
  "publish-page": "You launched your first campaign page",
  "connect-domain": "You connected your domain",
};

export function getWorkflow(slug: WorkflowSlug): Workflow {
  const workflow = WORKFLOWS.find((candidate) => candidate.slug === slug);
  if (!workflow) {
    throw new WorkflowNotDefinedError({
      message: `Workflow ${slug} is not defined`,
    });
  }
  return workflow;
}

/** Where a step's work happens. One kind today; a connected app or a form would join as another. */
export const workflowStepWorkSchema = z
  .discriminatedUnion("type", [
    z.object({
      type: z.literal("session"),
      sessionId: z.string().min(1),
      siteId: z.uuid(),
      route: z.string().startsWith("/"),
    }),
  ])
  .describe("WorkflowStepWork");

export type WorkflowStepWork = z.infer<typeof workflowStepWorkSchema>;

/** What a run stores about one step; a checked step is decided live (a store can disconnect), never stored here. */
export const workflowStepProgressSchema = z
  .object({
    /** When its work started, so any device can pick the step up where it is. */
    startedAt: z.string().optional(),
    /** When it was done: the merchant clicked it done, or its work finished. */
    doneAt: z.string().optional(),
    work: workflowStepWorkSchema.optional(),
  })
  .describe("WorkflowStepProgress");

export type WorkflowStepProgress = z.infer<typeof workflowStepProgressSchema>;

export const workflowRunProgressSchema = z
  .record(z.string(), workflowStepProgressSchema)
  .describe("WorkflowRunProgress");

export type WorkflowRunProgress = z.infer<typeof workflowRunProgressSchema>;

/** A step running in a chat session, with the page it works on. */
export type WorkflowStepSession = Extract<
  WorkflowStepWork,
  { type: "session" }
> & { startedAt: string };

export function findWorkflowStepSession(
  progress: WorkflowStepProgress | undefined,
): WorkflowStepSession | null {
  return progress?.work?.type === "session" && progress.startedAt
    ? { ...progress.work, startedAt: progress.startedAt }
    : null;
}

/** Every step running in a chat session, by step slug. */
export function getWorkflowStepSessions(
  progress: WorkflowRunProgress,
): Record<string, WorkflowStepSession> {
  return pickBy(
    mapValues(progress, findWorkflowStepSession),
    (session) => session !== null,
  );
}

/** Why Home leaves a workflow out: the project lacks a required integration, has one a more specific version is built for, or is offered another vertical's set. For `other-vertical`, `vertical` is the set the workflow belongs to, or, for a shared workflow a set leaves out, that set. */
export const workflowNotOfferedSchema = z
  .discriminatedUnion("reason", [
    z.object({
      reason: z.enum(["missing-integration", "has-integration"]),
      integration: z.enum(knownIntegrationKeys),
    }),
    z.object({
      reason: z.literal("other-vertical"),
      vertical: workflowVerticalSchema,
    }),
  ])
  .describe("WorkflowNotOffered");

export type WorkflowNotOffered = z.infer<typeof workflowNotOfferedSchema>;

/** `workflowNotOfferedSchema` as browsers released before it read it, which only knew Shopify. */
const workflowNotOfferedReasonSchema = z
  .enum(["not-on-shopify", "on-shopify"])
  .describe("WorkflowNotOfferedReason");

export type WorkflowNotOfferedReason = z.infer<
  typeof workflowNotOfferedReasonSchema
>;

/** One run as the server reports it. The browser also completes session steps from `progress` via `markStep`. */
export const workflowRunStateSchema = z
  .object({
    slug: workflowSlugSchema,
    /** A run row exists: someone on the project clicked into this workflow. */
    started: z.boolean(),
    /** Done per step slug: the check is true, or the manual step was clicked. */
    steps: z.record(z.string(), z.boolean()),
    /** The first step that is not done and not optional; null when finished. */
    next: z.string().nullable(),
    /** Every required step is done right now, whether or not a run exists. */
    finished: z.boolean(),
    /** Set the first time a started run finished. Survives facts regressing. */
    completedAt: z.string().nullable(),
    /** `completedAt` only for a finish the merchant worked for; a run created finished never celebrates. */
    earnedAt: z.string().nullable(),
    /** Who caused the finish; only their browser celebrates it. */
    earnedByUserId: z.string().nullable(),
    /** Credits on offer for finishing, already clamped to the cap; 0 once earned or capped. */
    rewardCredits: z.number().int().nonnegative(),
    /** Credits the first finish actually paid. */
    creditsPaid: z.number().int().nonnegative(),
    /** What the run stores per step slug; cleared when the run restarts. */
    progress: workflowRunProgressSchema,
    /** Why Home leaves this workflow out of its cards for the project; absent when it is offered. A link that names the workflow still opens it. */
    notOffered: workflowNotOfferedSchema.optional(),
    /** @deprecated `notOffered` for browsers released before it: set only when the integration is Shopify. */
    notOfferedReason: workflowNotOfferedReasonSchema.optional(),
  })
  .describe("WorkflowRunState");

export type WorkflowRunState = z.infer<typeof workflowRunStateSchema>;

export const workflowRunsSchema = z
  .record(workflowSlugSchema, workflowRunStateSchema)
  .describe("WorkflowRuns");

export type WorkflowRuns = z.infer<typeof workflowRunsSchema>;

function isWorkflowStepDone({
  step,
  progress,
  checks,
}: {
  step: WorkflowStep;
  progress: WorkflowRunProgress;
  checks: Partial<Record<SetupCheckName, { complete: boolean }>>;
}): boolean {
  return step.check
    ? Boolean(checks[step.check]?.complete)
    : Boolean(progress[step.slug]?.doneAt);
}

/** Pure, so the server and any test can evaluate a run without a database. */
export function evaluateWorkflowRun({
  workflow,
  progress,
  checks,
}: {
  workflow: Pick<WorkflowDefinition, "steps" | "completion" | "comingSoon">;
  progress: WorkflowRunProgress;
  checks: Partial<Record<SetupCheckName, { complete: boolean }>>;
}): { steps: Record<string, boolean>; next: string | null; finished: boolean } {
  const steps = Object.fromEntries(
    workflow.steps.map((step) => [
      step.slug,
      isWorkflowStepDone({ step, progress, checks }),
    ]),
  );
  const finished = isWorkflowFinished({ workflow, steps });
  const remaining =
    workflow.completion === "any"
      ? workflow.steps
      : workflow.steps.filter((step) => !step.optional);
  return {
    steps,
    next: finished
      ? null
      : (remaining.find((step) => !steps[step.slug])?.slug ?? null),
    finished,
  };
}

/** The one definition of "finished" the server and client share, so a card and its run never disagree. */
export function isWorkflowFinished({
  workflow,
  steps,
}: {
  workflow: Pick<WorkflowDefinition, "steps" | "completion" | "comingSoon">;
  steps: Record<string, boolean>;
}): boolean {
  if (workflow.comingSoon) {
    return false;
  }
  if (workflow.completion === "any") {
    return workflow.steps.some((step) => steps[step.slug]);
  }
  return workflow.steps
    .filter((step) => !step.optional)
    .every((step) => steps[step.slug]);
}
